import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        const result = await register(name, email, password);
        setLoading(false);
        if (result.success) {
            toast.success("Welcome! Your 3-day trial has started.");
            navigate("/dashboard");
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex">
            {/* Left panel */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#121212] border-r border-white/10 items-center justify-center p-12">
                <div className="max-w-md">
                    <div className="flex items-center gap-2 mb-8">
                        <BarChart3 className="w-8 h-8 text-[#007AFF]" />
                        <span className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Start Your Free Trial
                    </h2>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                        Get full access for 3 days. No credit card required.
                    </p>
                    <div className="space-y-3">
                        {["AI-powered entries & stop losses", "All market instruments", "Real-time price data"].map((item, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                <div className="w-1.5 h-1.5 bg-[#007AFF]" />
                                {item}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right panel - form */}
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <BarChart3 className="w-6 h-6 text-[#007AFF]" />
                        <span className="text-lg font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </div>

                    <h1 className="text-2xl font-bold tracking-tight text-white mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Create your account
                    </h1>
                    <p className="text-sm text-zinc-500 mb-8">3-day free trial, then $19/month</p>

                    {error && (
                        <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm px-4 py-3 mb-6" data-testid="register-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                        <div>
                            <Label className="text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 mb-2 block">Full Name</Label>
                            <Input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="bg-transparent border-white/10 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-xl text-white h-11"
                                placeholder="Your name"
                                required
                                data-testid="register-name-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 mb-2 block">Email</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="bg-transparent border-white/10 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-xl text-white h-11"
                                placeholder="you@example.com"
                                required
                                data-testid="register-email-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 mb-2 block">Password</Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="bg-transparent border-white/10 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-xl text-white h-11"
                                placeholder="Min 6 characters"
                                required
                                data-testid="register-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl h-11 font-bold"
                            data-testid="register-submit-btn"
                        >
                            {loading ? "Creating account..." : "Start Free Trial"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                    </form>

                    <p className="text-sm text-zinc-500 mt-6 text-center">
                        Already have an account?{" "}
                        <Link to="/login" className="text-[#007AFF] hover:text-[#3395FF] font-medium" data-testid="register-login-link">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
